import { z } from 'zod';
import { SportNameSchema, type SportName } from './profile.js';
import type { CountryCode } from './country.js';
import type { ConfidenceTier } from './skill.js';

/**
 * F32 — Open Games Marketplace.
 *
 * An open game decorates an existing F16 booking (1:1 via open_games.booking_id)
 * posted by the team captain (team_id set) or the solo booker (team_id NULL).
 * Other players discover it in the marketplace and join — atomically added to
 * booking_participants (role='guest'), synced into the F30 booking chat, and
 * decrementing the open spot count.
 *
 * Decisions: see plans/32_F32_open_games_marketplace.md (D1–D24).
 *   D1  Approval mode per game (FCFS default | captain_approval)
 *   D2  Skill enforcement = SOFT everywhere (advisory level range + ack warning)
 *   D3  Poster = team captain (team_id set) OR solo booker (team_id NULL)
 *   D4  Marketplace respects parent team privacy (invite_only → link-only)
 *   D5  Eligibility = sport profile + any rating (provisional allowed, flagged)
 *   D6  Timers: 48h request expiry + 12h leave-after-accept cutoff
 *   D7  Everything editable while status='open' (spots_total >= spots_filled)
 *   D8  'posted' alert = per-sport opt-in, default off
 *   D9  spots captain-set; spots_remaining derived (never stored)
 *   D10 Per-spot price informational only (off-platform; no platform settlement)
 *   D13 Join-request state machine: pending → approved|declined|withdrawn|expired
 *
 * Cross-bundler note: imports use `.js` extensions per the F03 cross-bundler
 * seam (consumed by both NodeNext API and Bundler/Metro web+mobile chains).
 */

// ─── Constants (D6 / D9 / D8) ───────────────────────────────────────────────

/** Captain-approval pending requests auto-expire after this many hours (D6). */
export const OPEN_GAME_REQUEST_EXPIRY_HOURS = 48;
/** An accepted joiner may leave up to this many hours before booking.start_at (D6). */
export const OPEN_GAME_LEAVE_CUTOFF_HOURS = 12;
/** A poster may hold at most this many games in status open|filled (D19). */
export const OPEN_GAME_MAX_CONCURRENT_PER_POSTER = 10;
/** Fallback alert radius (km) when a subscriber has no preferred_play_radius_km (D8). */
export const OPEN_GAME_DEFAULT_ALERT_RADIUS_KM = 25;

export const OPEN_GAME_SPOTS_MIN = 1;
export const OPEN_GAME_SPOTS_MAX = 30;
export const OPEN_GAME_TITLE_MAX = 80;
export const OPEN_GAME_DESCRIPTION_MAX = 500;
export const OPEN_GAME_MESSAGE_MAX = 280;
export const OPEN_GAME_DECLINE_REASON_MAX = 280;
export const OPEN_GAME_REPORT_DETAILS_MAX = 500;
export const OPEN_GAME_SUSPENSION_REASON_MAX = 500;
export const OPEN_GAME_CANCELLATION_NOTE_MAX = 200;

/** Public 1.0–7.0 skill scale bounds (shared with F31). */
export const OPEN_GAME_LEVEL_MIN = 1.0;
export const OPEN_GAME_LEVEL_MAX = 7.0;

// ─── Enums ──────────────────────────────────────────────────────────────────

/** open_games.status lifecycle. 'open' is the only state accepting joiners. */
export const OPEN_GAME_STATUS_VALUES = ['open', 'filled', 'closed', 'cancelled'] as const;
export type OpenGameStatus = (typeof OPEN_GAME_STATUS_VALUES)[number];
export const OpenGameStatusSchema = z.enum(OPEN_GAME_STATUS_VALUES);

/** Per-game approval mode (D1). */
export const OPEN_GAME_APPROVAL_MODES = ['fcfs', 'captain_approval'] as const;
export type OpenGameApprovalMode = (typeof OPEN_GAME_APPROVAL_MODES)[number];
export const OpenGameApprovalModeSchema = z.enum(OPEN_GAME_APPROVAL_MODES);

/** Join-request state machine (D13). */
export const JOIN_REQUEST_STATUS_VALUES = [
  'pending',
  'approved',
  'declined',
  'withdrawn',
  'expired',
] as const;
export type JoinRequestStatus = (typeof JOIN_REQUEST_STATUS_VALUES)[number];
export const JoinRequestStatusSchema = z.enum(JOIN_REQUEST_STATUS_VALUES);

/** Why an open game was cancelled (D14) — drives analytics + notification copy. */
export const OPEN_GAME_CANCELLATION_SOURCES = [
  'poster',
  'booking_cancelled',
  'team_disbanded',
] as const;
export type OpenGameCancellationSource = (typeof OPEN_GAME_CANCELLATION_SOURCES)[number];
export const OpenGameCancellationSourceSchema = z.enum(OPEN_GAME_CANCELLATION_SOURCES);

/** Marketplace sort options. 'nearest' requires lat+lng (else SORT_REQUIRES_GEO). */
export const OPEN_GAME_SORTS = ['soonest', 'nearest', 'newest'] as const;
export type OpenGameSort = (typeof OPEN_GAME_SORTS)[number];
export const OpenGameSortSchema = z.enum(OPEN_GAME_SORTS);

/** User report reasons (D20). */
export const OPEN_GAME_REPORT_REASONS = ['spam', 'inappropriate', 'misleading', 'other'] as const;
export type OpenGameReportReason = (typeof OPEN_GAME_REPORT_REASONS)[number];
export const OpenGameReportReasonSchema = z.enum(OPEN_GAME_REPORT_REASONS);

/** Report moderation status. */
export const OPEN_GAME_REPORT_STATUSES = ['open', 'reviewed', 'dismissed'] as const;
export type OpenGameReportStatus = (typeof OPEN_GAME_REPORT_STATUSES)[number];
export const OpenGameReportStatusSchema = z.enum(OPEN_GAME_REPORT_STATUSES);

/** Admin report resolution (non-'open' terminal states). */
export const OPEN_GAME_REPORT_RESOLUTIONS = ['reviewed', 'dismissed'] as const;
export type OpenGameReportResolution = (typeof OPEN_GAME_REPORT_RESOLUTIONS)[number];
export const OpenGameReportResolutionSchema = z.enum(OPEN_GAME_REPORT_RESOLUTIONS);

// ─── Error codes ────────────────────────────────────────────────────────────

export const OPEN_GAME_ERROR_CODES = [
  'GAME_NOT_FOUND',
  'GAME_FULL',
  'GAME_NOT_OPEN',
  'GAME_SUSPENDED',
  'ALREADY_JOINED',
  'SPORT_PROFILE_REQUIRED',
  'OUT_OF_RANGE_ACK_REQUIRED',
  'LEAVE_WINDOW_CLOSED',
  'NOT_A_PARTICIPANT',
  'SPOTS_BELOW_FILLED',
  'TOO_MANY_OPEN_GAMES',
  'NOT_GAME_OWNER',
  'BOOKING_NOT_ELIGIBLE',
  'OPEN_GAME_ALREADY_EXISTS',
  'REQUEST_NOT_FOUND',
  'REQUEST_NOT_PENDING',
  'NOT_REQUEST_OWNER',
  'INVALID_TRANSITION',
  'NOT_EDITABLE',
  'SORT_REQUIRES_GEO',
  'ALREADY_REPORTED',
] as const;
export type OpenGameErrorCode = (typeof OPEN_GAME_ERROR_CODES)[number];

// ─── Reusable field schemas ─────────────────────────────────────────────────

/** A 1.0–7.0 value with at most one decimal place (mirrors F31's level validation). */
const LevelValueSchema = z
  .number()
  .min(OPEN_GAME_LEVEL_MIN)
  .max(OPEN_GAME_LEVEL_MAX)
  .refine((v) => Number.isFinite(v) && Math.round(v * 10) === v * 10, {
    message: 'level value must have at most one decimal place',
  });

/** ISO 4217 3-letter currency code; defaults to USD (the only MVP pricing currency, F05). */
const SpotPriceCurrencySchema = z
  .string()
  .regex(/^[A-Z]{3}$/, 'currency must be a 3-letter ISO 4217 code')
  .default('USD');

// ─── Input schemas ──────────────────────────────────────────────────────────

/**
 * POST /me/bookings/:bookingId/open-game — post an open game from a booking.
 * bookingId is a route param. levelMin/levelMax are advisory (D2). spotPrice is
 * informational/off-platform (D10).
 */
export const PostOpenGameInputSchema = z
  .object({
    spotsTotal: z.number().int().min(OPEN_GAME_SPOTS_MIN).max(OPEN_GAME_SPOTS_MAX),
    approvalMode: OpenGameApprovalModeSchema.default('fcfs'),
    levelMin: LevelValueSchema.optional(),
    levelMax: LevelValueSchema.optional(),
    spotPriceMinor: z.number().int().nonnegative().optional(),
    spotPriceCurrency: SpotPriceCurrencySchema.optional(),
    title: z.string().trim().min(1).max(OPEN_GAME_TITLE_MAX).optional(),
    description: z.string().trim().min(1).max(OPEN_GAME_DESCRIPTION_MAX).optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.levelMin !== undefined && val.levelMax !== undefined && val.levelMax < val.levelMin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'levelMax must be greater than or equal to levelMin',
        path: ['levelMax'],
      });
    }
  });
export type PostOpenGameInput = z.infer<typeof PostOpenGameInputSchema>;

/**
 * PATCH /me/open-games/:id — edit while status='open' (D7). All fields optional.
 * spots_total cannot be set below the current spots_filled (→ SPOTS_BELOW_FILLED,
 * enforced at the service layer).
 */
export const UpdateOpenGameInputSchema = z
  .object({
    spotsTotal: z.number().int().min(OPEN_GAME_SPOTS_MIN).max(OPEN_GAME_SPOTS_MAX).optional(),
    approvalMode: OpenGameApprovalModeSchema.optional(),
    levelMin: LevelValueSchema.nullable().optional(),
    levelMax: LevelValueSchema.nullable().optional(),
    spotPriceMinor: z.number().int().nonnegative().nullable().optional(),
    spotPriceCurrency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional(),
    title: z.string().trim().min(1).max(OPEN_GAME_TITLE_MAX).nullable().optional(),
    description: z.string().trim().min(1).max(OPEN_GAME_DESCRIPTION_MAX).nullable().optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (
      val.levelMin !== undefined &&
      val.levelMin !== null &&
      val.levelMax !== undefined &&
      val.levelMax !== null &&
      val.levelMax < val.levelMin
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'levelMax must be greater than or equal to levelMin',
        path: ['levelMax'],
      });
    }
  });
export type UpdateOpenGameInput = z.infer<typeof UpdateOpenGameInputSchema>;

/**
 * POST /me/open-games/:id/join — request/join. FCFS resolves instantly;
 * captain_approval creates a pending request. outOfRangeAck must be true when
 * the joiner's rating is outside [levelMin, levelMax] (soft enforcement, D2 —
 * service returns OUT_OF_RANGE_ACK_REQUIRED otherwise). Idempotency-Key rides
 * in the request header, not the body (D12).
 */
export const RequestToJoinInputSchema = z
  .object({
    outOfRangeAck: z.boolean().default(false),
    message: z.string().trim().min(1).max(OPEN_GAME_MESSAGE_MAX).optional(),
  })
  .strict();
export type RequestToJoinInput = z.infer<typeof RequestToJoinInputSchema>;

/** POST /me/open-games/:id/requests/:reqId/decline — captain decline + optional reason. */
export const DeclineJoinRequestInputSchema = z
  .object({
    reason: z.string().trim().min(1).max(OPEN_GAME_DECLINE_REASON_MAX).optional(),
  })
  .strict();
export type DeclineJoinRequestInput = z.infer<typeof DeclineJoinRequestInputSchema>;

/** POST /me/open-games/:id/cancel — poster cancels; removes joiners + notifies (D13/D14). */
export const CancelOpenGameInputSchema = z
  .object({
    note: z.string().trim().min(1).max(OPEN_GAME_CANCELLATION_NOTE_MAX).optional(),
  })
  .strict();
export type CancelOpenGameInput = z.infer<typeof CancelOpenGameInputSchema>;

/**
 * GET /public/open-games — marketplace list. sport is required. sort='nearest'
 * requires lat+lng (else SORT_REQUIRES_GEO, D2/D4). priceMin/priceMax in minor
 * units. levelMin/levelMax narrow what you browse (never blocks a join, D2).
 */
export const MarketplaceQuerySchema = z
  .object({
    sport: SportNameSchema,
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    lat: z.coerce.number().min(-90).max(90).optional(),
    lng: z.coerce.number().min(-180).max(180).optional(),
    radiusKm: z.coerce.number().positive().max(500).optional(),
    priceMin: z.coerce.number().int().nonnegative().optional(),
    priceMax: z.coerce.number().int().nonnegative().optional(),
    levelMin: z.coerce.number().min(OPEN_GAME_LEVEL_MIN).max(OPEN_GAME_LEVEL_MAX).optional(),
    levelMax: z.coerce.number().min(OPEN_GAME_LEVEL_MIN).max(OPEN_GAME_LEVEL_MAX).optional(),
    sort: OpenGameSortSchema.default('soonest'),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(50).optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.sort === 'nearest' && (val.lat === undefined || val.lng === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'sort=nearest requires both lat and lng',
        path: ['sort'],
      });
    }
  });
export type MarketplaceQuery = z.infer<typeof MarketplaceQuerySchema>;

/** PUT /me/open-game-alerts/:sport — per-sport opt-in (D8). Body optional; sport in param. */
export const AlertSubscriptionInputSchema = z
  .object({
    sport: SportNameSchema,
  })
  .strict();
export type AlertSubscriptionInput = z.infer<typeof AlertSubscriptionInputSchema>;

/** POST /me/open-games/:id/report — report a listing (D20). */
export const ReportOpenGameInputSchema = z
  .object({
    reason: OpenGameReportReasonSchema,
    details: z.string().trim().min(1).max(OPEN_GAME_REPORT_DETAILS_MAX).optional(),
  })
  .strict();
export type ReportOpenGameInput = z.infer<typeof ReportOpenGameInputSchema>;

/** POST /admin/open-games/:id/suspend — admin suspend (D20). */
export const SuspendOpenGameInputSchema = z
  .object({
    reason: z.string().trim().min(1).max(OPEN_GAME_SUSPENSION_REASON_MAX),
  })
  .strict();
export type SuspendOpenGameInput = z.infer<typeof SuspendOpenGameInputSchema>;

/** POST /admin/open-game-reports/:id/resolve — admin resolve a report. */
export const ResolveOpenGameReportInputSchema = z
  .object({
    resolution: OpenGameReportResolutionSchema,
  })
  .strict();
export type ResolveOpenGameReportInput = z.infer<typeof ResolveOpenGameReportInputSchema>;

// ─── Entity read shapes ─────────────────────────────────────────────────────

/** Compact card in the marketplace list + "my games". spotsRemaining is derived (D9). */
export interface OpenGameSummary {
  readonly id: string;
  readonly bookingId: string;
  readonly teamId: string | null;
  readonly postedByUserId: string;
  readonly sport: SportName;
  readonly venueId: string;
  readonly venueName: string;
  readonly venueLat: number;
  readonly venueLng: number;
  readonly startAt: string;
  readonly endAt: string;
  readonly spotsTotal: number;
  readonly spotsFilled: number;
  readonly spotsRemaining: number;
  readonly levelMin: number | null;
  readonly levelMax: number | null;
  readonly spotPriceMinor: number | null;
  readonly spotPriceCurrency: string;
  readonly approvalMode: OpenGameApprovalMode;
  readonly status: OpenGameStatus;
  readonly title: string | null;
  /** Haversine distance from the query point, populated only when lat+lng supplied. */
  readonly distanceKm: number | null;
  readonly createdAt: string;
}

/** The viewer's relationship to a game, for the detail screen's CTA state. */
export interface OpenGameViewerState {
  readonly isPoster: boolean;
  readonly isParticipant: boolean;
  readonly requestStatus: JoinRequestStatus | null;
  /** True when the viewer's rating for the sport is outside [levelMin, levelMax]. */
  readonly outOfRange: boolean;
  /** False when the viewer has no F08 sports_profile row for the sport (D5). */
  readonly eligible: boolean;
}

/** Full detail returned by GET /public/open-games/:id (direct-link safe, D4). */
export interface OpenGameDetail extends OpenGameSummary {
  readonly description: string | null;
  readonly teamName: string | null;
  readonly postedByDisplayName: string | null;
  /** F09 v2 — the poster's PLAYER tick ("Hosted by ✓"). */
  readonly postedByVerified: boolean;
  /** Display names of accepted joiners (off-platform price notice rendered alongside). */
  readonly participants: readonly OpenGameParticipantView[];
  /** Present only for an authenticated viewer. */
  readonly viewer: OpenGameViewerState | null;
  readonly suspended: boolean;
}

/** A confirmed participant (host + accepted guests) shown on the detail screen. */
export interface OpenGameParticipantView {
  readonly userId: string;
  readonly displayName: string | null;
  readonly role: 'booker' | 'guest';
  /** F09 v2 — the participant's PLAYER tick. */
  readonly verified: boolean;
}

/** One join request — the joiner's "my requests" view. */
export interface JoinRequestView {
  readonly id: string;
  readonly openGameId: string;
  readonly requesterUserId: string;
  readonly status: JoinRequestStatus;
  readonly approvalModeAtRequest: OpenGameApprovalMode;
  readonly skillInRange: boolean;
  readonly outOfRangeAck: boolean;
  readonly message: string | null;
  readonly declineReason: string | null;
  readonly decidedAt: string | null;
  readonly expiresAt: string | null;
  readonly createdAt: string;
  /**
   * The game this request is for (title, sport, venue, start) so "My requests"
   * can say which game it is. Set by `GET /me/open-game-requests`; optional for
   * other projections / fixtures.
   */
  readonly game?: OpenGameRequestGameSummary | null;
}

export interface OpenGameRequestGameSummary {
  readonly title: string | null;
  readonly sportName: string;
  readonly venueName: string;
  readonly startAt: string;
  readonly timezone: string;
}

/** A captain-inbox row: the request enriched with the requester's skill signal (D5). */
export interface CaptainInboxItem extends JoinRequestView {
  readonly requesterDisplayName: string | null;
  /** F09 v2 — the requester's PLAYER tick. */
  readonly requesterVerified: boolean;
  readonly effectiveSkillValueAtRequest: number | null;
  readonly skillConfidenceAtRequest: ConfidenceTier | null;
}

/** One row in open_game_alert_subscriptions (D8). */
export interface OpenGameAlertSubscription {
  readonly userId: string;
  readonly sport: SportName;
  readonly countryCode: CountryCode;
  readonly createdAt: string;
}

/** One row in open_game_reports (D20). */
export interface OpenGameReport {
  readonly id: string;
  readonly openGameId: string;
  readonly reporterUserId: string;
  readonly reason: OpenGameReportReason;
  readonly details: string | null;
  readonly status: OpenGameReportStatus;
  readonly resolvedByAdminId: string | null;
  readonly resolvedAt: string | null;
  readonly countryCode: CountryCode;
  readonly createdAt: string;
}

/** Admin list row: a game enriched with its open-report count + suspension state. */
export interface AdminOpenGameListItem extends OpenGameSummary {
  readonly suspendedAt: string | null;
  readonly openReportCount: number;
}

/** Cursor-paginated marketplace response. */
export interface OpenGameListResponse {
  readonly data: readonly OpenGameSummary[];
  readonly meta: {
    readonly appliedSort: OpenGameSort;
    readonly nextCursor: string | null;
  };
}

// ─── F32 NotificationEvent payload shapes (consumed by the orchestrator) ─────

/** Recipient row reused across the 6 F32 open_game.* notification events. */
export interface F32NotificationRecipient {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string | null;
}

/** Context attached to every open-game notification. */
export interface F32NotificationGameContext {
  readonly openGameId: string;
  readonly bookingId: string;
  readonly sportName: SportName;
  readonly venueName: string;
  readonly startAt: string;
  readonly title: string | null;
}
